// apps/web/src/components/issues/IssuesManager.tsx
// Issues tracking component

import type React from 'react';
import { useState, useEffect } from 'react';
import type { Issue } from '../../types/issue';
import { apiClient } from '../../lib/apiClient';

export function IssuesManager() {
  const [issues, setIssues] = useState<Issue[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);

  useEffect(() => {
    const fetchIssues = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get('/api/v1/issues');
        setIssues(response.data.issues);
      } catch (err) {
        setError('Failed to load issues');
        console.error('Error loading issues:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchIssues();
  }, []);

  const handleCreateIssue = async (issueData: any) => {
    try {
      const response = await apiClient.post('/api/v1/issues', issueData);
      setIssues([response.data, ...issues]);
      setShowCreateForm(false);
    } catch (err) {
      setError('Failed to create issue');
      console.error('Error creating issue:', err);
    }
  };

  if (loading) {
    return <div className="loading">Loading issues...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="issues-manager">
      <div className="header">
        <h2>Issues Management</h2>
        <button 
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="btn btn-primary"
        >
          {showCreateForm ? 'Cancel' : 'Create Issue'}
        </button>
      </div>

      {showCreateForm && (
        <IssueForm 
          onSubmit={handleCreateIssue} 
          onCancel={() => setShowCreateForm(false)} 
        />
      )}

      <div className="issues-grid">
        {issues.map(issue => (
          <IssueCard key={issue.id} issue={issue} />
        ))}
      </div>
    </div>
  );
}

// Helper component
function IssueCard({ issue }: { issue: Issue }) {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'OPEN': return 'status-open';
      case 'IN_PROGRESS': return 'status-in-progress';
      case 'RESOLVED': return 'status-resolved';
      case 'CLOSED': return 'status-closed';
      case 'BLOCKED': return 'status-blocked';
      default: return 'status-default';
    }
  };

  return (
    <div className="issue-card">
      <div className="issue-header">
        <h3>{issue.title}</h3>
        <span className={`status-badge ${getStatusColor(issue.status)}`}>
          {issue.status}
        </span>
      </div>
      <p>{issue.description || 'No description'}</p>
      <p><strong>Type:</strong> {issue.issueType}</p>
      <p><strong>Priority:</strong> {issue.priority}</p>
      <p><strong>Reporter:</strong> {issue.reporterId.substring(0, 8)}...</p>
      <p><strong>Created:</strong> {new Date(issue.createdAt).toLocaleDateString()}</p>
      <div className="actions">
        <button className="btn btn-secondary">View</button>
        <button className="btn btn-warning">Assign</button>
        <button className="btn btn-primary">Resolve</button>
      </div>
    </div>
  );
}

// Helper component
function IssueForm({ onSubmit, onCancel }: { onSubmit: any; onCancel: any }) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    issueType: 'BUG',
    priority: 'MEDIUM',
    assigneeId: '',
    reporterId: ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="issue-form">
      <h3>Create New Issue</h3>
      <input
        type="text"
        placeholder="Issue Title"
        value={formData.title}
        onChange={(e) => setFormData({...formData, title: e.target.value})}
        required
      />
      <textarea
        placeholder="Description"
        value={formData.description}
        onChange={(e) => setFormData({...formData, description: e.target.value})}
      />
      <select
        value={formData.issueType}
        onChange={(e) => setFormData({...formData, issueType: e.target.value})}
      >
        <option value="BUG">Bug</option>
        <option value="FEATURE">Feature</option>
        <option value="ENHANCEMENT">Enhancement</option>
        <option value="TASK">Task</option>
        <option value="SECURITY">Security</option>
        <option value="PERFORMANCE">Performance</option>
      </select>
      <select
        value={formData.priority}
        onChange={(e) => setFormData({...formData, priority: e.target.value})}
      >
        <option value="LOW">Low</option>
        <option value="MEDIUM">Medium</option>
        <option value="HIGH">High</option>
        <option value="CRITICAL">Critical</option>
      </select>
      <input
        type="text"
        placeholder="Assignee ID (optional)"
        value={formData.assigneeId}
        onChange={(e) => setFormData({...formData, assigneeId: e.target.value})}
      />
      <input
        type="text"
        placeholder="Reporter ID"
        value={formData.reporterId}
        onChange={(e) => setFormData({...formData, reporterId: e.target.value})}
        required
      />
      <div className="form-actions">
        <button type="submit" className="btn btn-primary">Create Issue</button>
        <button type="button" onClick={onCancel} className="btn btn-secondary">Cancel</button>
      </div>
    </form>
  );
}